import { Alert, Button, Form, Input } from "antd";
import UnloggedLayout from "components/UnloggedLayout";
import { AuthContext } from "contexts";
import { ReactElement, useContext, useState } from "react";
import { FormattedMessage } from "react-intl";
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import authService from "services/authService";
import errorHandler from "utils/errorHandler";
import "./index.css";
import { cn } from "utils/cn";

function LoginPage() {
  const [error, setError] = useState<ReactElement | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const { logIn } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  const from = ((location.state as any)?.from.pathname as string) || "/";

  const forgotPassword = searchParams.get("forgotPassword") === "true";
  const invalidResetTokenError =
    searchParams.get("invalidResetTokenError") === "true";
  const emailAlreadyTaken = searchParams.get("emailAlreadyTaken") === "true";
  const emailNotConfirmed = searchParams.get("emailNotConfirmed") === "true";

  const onFinish = (values: { email: string; password: string }) => {
    const { email, password } = values;
    setLoading(true);

    authService
      .login({
        identifier: email.trim(),
        password: password,
      })
      .then(({ jwt, user }) => {
        logIn(jwt, user);
        navigate(from, { replace: true });
      })
      .catch((e) => {
        if (e?.response?.data?.message?.[0]?.messages?.[0]?.id) {
          setError(
            <FormattedMessage
              id={e?.response?.data?.message?.[0]?.messages?.[0]?.id || -1}
              defaultMessage="Un problème est survenu. Veuillez rafraîchir la page ou essayer ultérieurement."
              description="Server response to login"
            />
          );
          setLoading(false);
        } else {
          throw e;
        }
      })
      .catch(errorHandler(setError));
  };

  const Button = ({ text, className }: { text: string, className: string }) => {
    return (
      <button className={cn("bg-[#6633cc] hover:bg-[#5b31af] transition-all text-white py-3 px-12 rounded-full", className)}>
        {text  && <p className={cn()}>{text}</p>}
      </button>
    );
  }

  return (
    <div className="w-full flex h-screen">
      <div className="w-5/12 hidden xl:block relative overflow-hidden">
        <div className="bg-[#6633cc] w-full h-full"></div>
        <div className="bg-[#c2eeff] w-[2000px] h-[1000px] absolute bottom-[-500px] 3xl:bottom-[-200px] left-[-100px] rotate-[-15deg]"></div>
        <div className="bg-[#c2eeff] w-full h-[787px] absolute bottom-[-450px] left-[-150px] rounded-full hidden"></div>
        <div className="text-[#c2eeff] before:content-['◂'] text-[2000px] absolute top-[-1050px] right-[-220px] hidden"></div>
        <img src="/login-img-2.png" alt="écrans tréso" className="absolute ml-auto mr-auto bottom-0 left-0 right-0 z-10 w-12/12" />
        <img src="/login-img.png" alt="pilotez la fusée!" className="absolute ml-auto mr-auto top-[125px] left-0 right-0 z-10 w-8/12" />
        <img src="/logo-big.png" alt="logo zenfirst" className="absolute ml-auto mr-auto top-[30px] left-0 right-0 z-10 w-3/12" />
      </div>
      <div className="w-full xl:w-7/12 flex flex-col items-center justify-center px-2 lg:px-10 xl:px-36">
        <div className="bg-slate-50 border-slate-100 border-2 rounded-3xl px-2 py-2 xl:px-28 xl:py-12 w-full xl:w-fit h-fit flex flex-col justify-center items-center">
          <p className="font-bold text-2xl text-black text-center w-full">Bienvenue !</p>
          <div className="py-2"></div>
          <p className="text-md text-black text-center w-full">J'ai déjà un compte</p>
          <div className="py-1"></div>
          <a href={`${process.env.REACT_APP_BASE_URL}connect/auth0`}><Button className="m-auto" text="Connexion" /></a>
        </div>
        <div className="py-4"></div>
        <div className="w-full flex flex-col justify-center items-center">
          <div className="flex">
            <div className="relative">
              <img src="/gyro.png" alt="gyrophare" className="w-24 h-20" />
              <div className="signal absolute top-4 bottom-0 left-0 right-0 ml-auto mr-auto"></div>
            </div>
            <p className="font-bold text-xl text-black text-left py-2">Important ! <br /> Changement de méthode de connexion</p>
          </div>
          <div className="flex justify-center items-center gap-5 py-2">
            <div className="">
              <p className="text-lg">Si vous vous connectiez avec :</p>
              <p className="py-3"></p>
              <p className="text-lg">Utilisez ce bouton et, à l'étape <br /> suivante, cliquez sur "<a href={`${process.env.REACT_APP_BASE_URL}connect/auth0`}><span className="text-[#007bad]">Inscription</span></a>" </p>
            </div>
            <img src="/old-login.jpg" alt="Ancien login form" className="shadow-2xl w-5/12" onClick={() => {
              window.location.href = `https://${process.env.REACT_APP_AUTH0_SUBDOMAIN}.auth0.com/v2/logout?federated&returnTo=${encodeURI(`${process.env.REACT_APP_DOMAIN}/logout&client_id=${process.env.REACT_APP_AUTH0_CLIENT_ID}`)}`;
            }} />
          </div>
          <div className="py-3"></div>
          <a href={`${process.env.REACT_APP_BASE_URL}connect/auth0`}><Button className="m-auto" text="Mettre à jour le mode de connexion" /></a>
          <p className="py-2"></p>
          <p className="text-sm text-center">Cela vous permettra d'utiliser <span className="font-bold">un mot de passe unique pour tous les <br /> outils Zenfirst</span> et d'accéder à votre espace en toute sécurité.</p>
        </div>
      </div>
    </div>
  );
  /*return (
    <UnloggedLayout>
      <div className="form-container">

        <div id="auth0" style={{marginBottom: 50}}>
          <p style={{textAlign: "left"}}>
          <b>Vous utilisez Zenfirst tréso et Zenfirst gesco ?</b>
        </p>
        <p style={{ marginTop: 15, textAlign: "left" }}>
          Découvrez le nouveau service d'authentification unique pour simplifier
          votre connexion aux outils Zenfirst
        </p>
        <p style={{ marginTop: 15, marginBottom: 10, textAlign: "left" }}>
          <b>✓ Un seul mot de passe pour toutes les applications</b>
        </p>
          <Button
            type="primary"
            htmlType="submit"
            className="FormButton"
            block
            onClick={() => {
              window.location.href = `${process.env.REACT_APP_BASE_URL}connect/auth0`;
            }}
          >
            Me connecter aux outils Zenfirst
          </Button>
        </div>

        <h1>Me connecter</h1>
        <p>
          Vous n'avez pas de compte ?{" "}
          <Link to="/register">Créer un compte</Link>
        </p>
        {invalidResetTokenError && !error && (
          <Alert
            message="Code de réinitialisation invalide ou expiré."
            type="error"
          />
        )}
        {forgotPassword && !error && (
          <Alert
            message="Vous allez recevoir un email avec les instructions pour réinitialiser votre mot de passe."
            type="success"
          />
        )}
        {emailAlreadyTaken && !error && (
          <Alert
            message="Cet email est déjà lié à un compte. Utilisez-vous un mot de passe ?"
            type="error"
          />
        )}
        {emailNotConfirmed && !error && (
          <Alert
            message="Votre email n'a pas été vérifié. Avez-vous consulté votre boîte email pour le confirmer ?"
            type="error"
          />
        )}
        {error && <Alert type="error" message={error} />}
        <Form
          name="login"
          onFinish={onFinish}
          className="login-form"
          layout="vertical"
          requiredMark={false}
        >
          <Form.Item
            label="Votre email"
            id="email"
            name="email"
            rules={[
              { required: true, message: "Requis" },
              { type: "email", message: "Email invalide" },
            ]}
          >
            <Input
              type="email"
              autoComplete="username email"
              placeholder="Saisissez votre adresse e-mail"
            />
          </Form.Item>
          <Form.Item
            id="password"
            name="password"
            label="Mot de passe"
            rules={[{ required: true, message: "Requis" }]}
          >
            <Input.Password
              autoComplete="current-password"
              visibilityToggle={false}
              placeholder="••••••••"
            />
          </Form.Item>
          <Button
            type="default"
            htmlType="submit"
            className="FormButton"
            loading={loading}
            block
          >
            Me connecter
          </Button>
        </Form>{" "}
        <p>
          <Link to="/forgot">J'ai oublié mon mot de passe</Link>
        </p>
      </div>
    </UnloggedLayout>
  );*/
}

export default LoginPage;
